import i18n from '@/i18n'
import type { SnowflakeId } from '@/types/snowflake'
import type { EntryContextWire } from './entryContext'
import type { McpInvalidField, McpToolCard } from './mcpToolCard'

/**
 * 确认卡片里一个字段的渲染/回填类型（后端 draft.fields[].type）。
 * `datetime`：值是 UTC ISO，卡片按用户时区显示 `<input type="datetime-local">`，回传 UTC ISO（见 datetime.ts）。
 */
export type ConfirmationFieldType = 'text' | 'textarea' | 'select' | 'date' | 'datetime' | 'number' | 'readonly'

export interface ConfirmationFieldOption {
  value: string
  label: string
}

export interface ConfirmationField {
  key: string
  label: string
  value: unknown
  type: ConfirmationFieldType
  editable: boolean
  options?: ConfirmationFieldOption[]
}

/** 后端把模型的写工具参数映射成的「轻量表单」形状。 */
export interface ConfirmationDraft {
  /** 'task_create' | 'service_request' | …（只做展示 / 路由映射，不穷举） */
  kind: string
  summary: string
  fields: ConfirmationField[]
  /** 「Open in form」用：完整建单表单的路由 + 预填数据 */
  form?: { route: string; initialData?: Record<string, unknown> }
}

/**
 * 确认卡上的疑似重复单（api `TaskDuplicateService.confirmable_matches` 的一项，snake_case wire）。
 * taskId 是 Snowflake，一律字符串。
 */
export interface PossibleDuplicate {
  taskId: SnowflakeId
  title: string | null
  status: string | null
  /** 后端给的站内路由 `/tasks/<id>`；缺省则不渲染链接 */
  webPath: string | null
}

/**
 * 确认卡是哪一类（`confirmation_required.confirm_kind`）。
 * ⚠️ 与 `draft.kind`（task_create / service_request / …）**不是同一个字段**——
 * 后端刻意没叫 `kind`，前端也别合并。缺省 = `write_tool`（老后端 / 默认值）。
 */
export type ConfirmKind = 'write_tool' | 'flow_plan' | 'mcp_tool'

/**
 * 查重结论（`draft.duplicate_check`，main-api ed9ff67db）。
 * found=有候选 / degraded=查了但查不动 / skipped=确知没去查（无任务读取权限；Flow 卡不会出现）/
 * none_found=**真查过**、没有候选（ec39307c9 起，可以说「未发现重复」）。
 * **`null`（不带键）= 不下结论**：条件不足没查，或相似任务都被判为不是同一件事——界面**不得**据此说「未发现重复」。
 */
export type DuplicateCheck = 'found' | 'degraded' | 'skipped' | 'none_found'

/** 一次待确认的写操作（confirmation_required 事件的载荷）。 */
export interface PendingConfirmation {
  toolUseId: string
  /** 工具名，如 create_task */
  name: string
  /** 模型给出的原始参数（审计用，不直接渲染） */
  input: Record<string, unknown>
  draft: ConfirmationDraft
  /** ISO 8601；后端 pending TTL 15 min。缺失表示不做倒计时 */
  expiresAt: string | null
  confirmKind: ConfirmKind
  /**
   * 疑似重复单。**`null` = 这张卡没有查重结论**（没查 / 查重降级 / 无权限跳过），
   * 不带键时**分不清**「查了没有 / 降级 / 无权限跳过」（B3 2026-09-12 确认），所以界面绝不说「未发现重复」。
   * 唯一来源 = `draft.possible_duplicates`（两种卡都有）；顶层那份 C0 裁决撤回，不读。
   */
  possibleDuplicates: PossibleDuplicate[] | null
  duplicateCheck: DuplicateCheck | null
  /**
   * flow_plan 卡的草稿前端**画不出来**（不认识的 `draft.kind` / 带了不认识的 `version`）→ 卡上不给 Confirm。
   * 只在为 true 时带这个键。起因：B4 reschedule 草稿（契约草案 90e32f36f）没有 summary / fields，
   * 按写工具卡解析会变成「空摘要 + Confirm」，点下去就真的执行整天重排。
   */
  draftUnrenderable?: true
  /**
   * reschedule 计划卡（flow_plan 的 draft.kind = reschedule_day | reschedule_undo）解析后的草稿。
   * 有它时卡片按它渲染、Confirm 不带 params；`draftUnrenderable` 仍决定给不给 Confirm（空列表时为 true）。
   */
  planCard?: PlanCardDraft
  /**
   * 租户连接器工具卡（confirm_kind = mcp_tool，main-api B6 T6）解析后的草稿。
   * 有它时卡片按 form_schema 渲染、Confirm 只带用户改过的表单字段；解析失败时不带它、`draftUnrenderable` 为 true。
   */
  mcpCard?: McpToolCard
}

// ─────────────────────────────────────────────── reschedule 计划卡（main-api 713edaf02 reschedule_card_schema.py，契约 v1.0.1）

/** 实体引用：只显示 label；label 为 null 用通用名，**不显示 id** */
export interface PlanCardEntityRef { id: string; label: string | null }
/** 一句面向用户的话：code + params。未知 code 由渲染层按区块兜底 */
export interface PlanCardText { code: string; params: Record<string, unknown> }
/** 一条任务在某一侧的指派 + 开始时间（ISO UTC；null = 未指派 / 未排期） */
export interface PlanCardSlot { assignee: PlanCardEntityRef | null; startAt: string | null }
export interface PlanCardMetrics {
  placed: number; unplaced: number; moved: number; lateCount: number
  travelSec: number; travelMeters: number; makespanSec: number; utilizationPct: number
}
export interface RescheduleMove {
  itemId: string
  task: PlanCardEntityRef
  listing: PlanCardEntityRef | null
  before: PlanCardSlot
  after: PlanCardSlot
  afterEndAt: string
  durationSec: number
  durationEstimated: boolean
  why: PlanCardText
}
export interface RescheduleUnplaced { task: PlanCardEntityRef; reason: PlanCardText }
export interface RescheduleRevert { itemId: string; task: PlanCardEntityRef; from: PlanCardSlot; to: PlanCardSlot }
export interface RescheduleDayDraft {
  kind: 'reschedule_day'
  version: 1
  title: PlanCardText
  date: string
  tz: string
  /** 后端保证按 after.assignee.id（未指派最后）→ after.startAt → itemId 排序，最多 1000 条 */
  moves: RescheduleMove[]
  unplaced: RescheduleUnplaced[]
  metrics: { before: PlanCardMetrics | null; after: PlanCardMetrics }
  notes: PlanCardText[]
  warnings: PlanCardText[]
  /** 未经业务确认的规则：不可折叠、不可摘除 */
  disclaimers: PlanCardText[]
  editable: string[]
}
export interface RescheduleUndoDraft {
  kind: 'reschedule_undo'
  version: 1
  title: PlanCardText
  sourceRunId: string
  /** null 时标题是 reschedule_undo.card.title_undated */
  date: string | null
  tz: string
  reverts: RescheduleRevert[]
  notes: PlanCardText[]
  editable: string[]
}
export type PlanCardDraft = RescheduleDayDraft | RescheduleUndoDraft

// ─────────────────────────────────────────────────────────────── Flow / ui_block
// 真相源：main-api b8c7a6316 `flow_event_schema.py` / `ui_block_schema.py`（wire camelCase）。

/** 多语言包：后端不挑语言，前端按用户语言挑，缺失回落 en。 */
export type I18nText = Partial<Record<'en' | 'zh' | 'es' | 'pt', string>>

/** api `FlowStepStatus` 闭集。未知值在解析层丢弃整帧。 */
export type FlowStepStatus = 'running' | 'done' | 'skipped' | 'needs_input' | 'failed'

/**
 * 被这条 run 取代的那张停着的草稿（`flow_step.supersedes`，main-api 5908f18d8）。
 * 只出现在一条 run 的**第一帧**；没有取代时后端不带这个键 → 前端为 null。
 */
export interface SupersededDraft {
  runId: SnowflakeId
  /** 旧草稿的标题；旧草稿还没有标题时后端不带（exclude_none）→ null */
  title: string | null
}

/** `flow_step` 事件的一帧。Paused / Failed 帧的 title 是 `{}`。 */
export interface FlowStep {
  flowId: string
  runId: SnowflakeId
  stepId: string
  title: I18nText
  method: string
  status: FlowStepStatus
  /** ⚠️ 过渡字段：后端拼的中文。**显示请用 flowStepSummaryText**（按 summaryCode 选模板），只有无代码时才退回它 */
  summary: string | null
  supersedes: SupersededDraft | null
  /** 结果 / 追问 / 失败原因的稳定代码（main-api 80c2d7243）；没有代码时后端不带键 → null */
  summaryCode: string | null
  /** 模板参数；实体一律 {id: str, label: str|null}，只显示 label */
  summaryParams: Record<string, unknown> | null
}

/** 动作白名单（api `UiAction`）。闭集之外的 kind 在解析层即为 null。 */
export type UiAction =
  | { kind: 'navigate'; route: string; params: Record<string, string> | null }
  | { kind: 'open_flyout'; entity: string; entityId: SnowflakeId }
  | { kind: 'start_flow'; flowId: string; params: Record<string, unknown> | null }
  | { kind: 'copy'; text: string }
  | { kind: 'download'; assetId: SnowflakeId }

export interface UiChip {
  /** 按钮文案代码（main-api 918cfe6d9，如 chip.task.open_form）；有码时 label 只是英文调试文案，不上屏 */
  code?: string | null
  label: string
  action: UiAction
}

/** `ui_block` 闭集。未知 type 在解析层即为 null。 */
export type UiBlock =
  | { type: 'entity_link'; entity: string; id: SnowflakeId; label: string; route: string }
  | { type: 'action_chips'; items: UiChip[] }
  /** titleCode / labelCode（main-api c57bae45d）：有码时 title / label 只是英文调试字段，不上屏 */
  | { type: 'result_card'; title: string; titleCode?: string | null; fields: Array<{ label: string; labelCode?: string | null; value: string }>; primary: UiChip | null }
  /** 确认之后没成功的条目（c57bae45d，B4 契约 §8）：只列 skipped / failed；truncated = 被截掉的条数 */
  | { type: 'apply_report'; flowId: string; status: string; items: ApplyReportItem[]; truncated: number }

export interface ApplyReportItem {
  entity: string
  id: SnowflakeId
  /** 后端批量查的标题；查不到 / 没读权限 = null（界面给占位，不露 id） */
  label: string | null
  outcome: 'skipped' | 'failed'
  /** failed → apply.ERROR_CODES；skipped → SKIP_REASONS。闭集，按码出前端文案 */
  code: string
}

/** 一条 Flow run 在消息流里的聚合进度（按 runId 聚合，同 stepId 后到覆盖先到）。 */
export interface FlowProgress {
  flowId: string
  runId: SnowflakeId
  steps: FlowStep[]
  /** 这条 run 取代了哪张停着的草稿（取自它第一帧的 supersedes）；没有取代时缺省 */
  supersedes?: SupersededDraft
  /** 这条 run 自己已被后来的 run 取代（后端已按 superseded 收尾） */
  superseded?: boolean
}

/** `GET /ai/assistant/suggestions` 的一项（api `SuggestionItem`）。 */
export interface AssistantSuggestion {
  id: string
  kind: 'flow' | 'playbook'
  title: I18nText
  /** 给用户看的一句话（main-api c3c85eac7 起是多语言包，与 title 同形）；拿不到时为 {}，不显示 */
  description: I18nText
}

export type ConfirmationStatus =
  | 'pending' // 等用户决策
  | 'submitting' // 已点 Confirm / Cancel，请求在途
  | 'confirmed' // allow 成功
  | 'cancelled' // deny 成功
  | 'expired' // 倒计时归零或后端 409（not_found / mismatch / expired）
  | 'failed' // allow 的续流里写操作失败（tool_result.ok === false 或 error 事件）——没写进去

export interface ConfirmationEntry {
  pending: PendingConfirmation
  status: ConfirmationStatus
  /** status='failed' 时续流里 tool_result 带的 error_code（没有 = 写工具卡 / 老后端 / error 事件） */
  failureCode?: string | null
  /**
   * mcp_tool 卡确认被 400 退回（参数不合审批快照里的 schema）时后端指出的字段（main-api 3fbb02ea1 `details.errors[]`）。
   * 卡片回到 pending、这些字段按 `code` 出前端文案并标红；再次提交时清空。后端的英文 message 不上卡（C0 2026-09-14）。
   */
  invalidFields?: McpInvalidField[]
}

/**
 * 本轮回答实际引用到的知识库文章（`sources` 事件的一项）。
 * 只有正文里出现过 `[[kb:<id>]]` 标记的文章才会下发，顺序 = 首次被引用的顺序。
 */
export interface ChatSource {
  /** knowledge unit 的 SnowflakeId（字符串，禁止 Number() 转换） */
  id: string
  title: string
  /** 后端 lookup 值（FAQ / POLICY / …），缺省则不渲染类型徽标 */
  knowledgeType?: string
  /** 适用范围（listing / community / …），只作为 tooltip 的补充信息 */
  appliesTo?: string
  /** 文章最后更新时间（ISO 8601），面板里显示相对时间；缺省则不显示日期 */
  updatedAt?: string
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  /**
   * 'confirmation' → ConfirmationCard；'flow_progress' → FlowProgressCard；'ui_block' → UiBlockRenderer；
   * 'notice' → 前端自己加的提示条（F5 恢复：被刷新打断 / 更早的消息未保存），不是后端内容。
   * 非 text 的几种 content 都不用。
   */
  kind?: 'text' | 'confirmation' | 'flow_progress' | 'ui_block' | 'notice'
  /** kind='notice'：哪种提示 */
  notice?: 'interrupted' | 'truncated'
  /** notice='interrupted'：被打断那一轮用户问的话，「重新发送」用（不自动重发） */
  resendText?: string
  confirmation?: ConfirmationEntry
  /** kind='confirmation' 时：同一轮里 status=failed 的 Flow 步骤（卡上必须看得出「这一步没成功」） */
  failedSteps?: FlowStep[]
  flowProgress?: FlowProgress
  blocks?: UiBlock[]
  /** 本条回答引用的知识库文章（气泡下方 Sources 条），无引用时缺省 */
  sources?: ChatSource[]
}

/**
 * 当前这一轮是怎么起的（F3 阶段 4）：刷新打断后据此决定能不能「重新发送」——
 * 只有普通消息能原样重发；start_flow 的用户气泡是按钮文字、cancel_flow 没有用户气泡、确认续流重发会重复写。
 */
export type ChatTurn = { kind: 'message'; text: string } | { kind: 'start_flow' } | { kind: 'cancel_flow' } | { kind: 'confirm' }

/** 纯文本消息（kind 缺省或 'text'）。卡片类消息的 content 恒为空，不能按「空气泡」处理。 */
export const isTextMessage = (m: ChatMessage): boolean => m.kind === undefined || m.kind === 'text'

export type SSEEvent =
  | { type: 'token'; text: string }
  | { type: 'tool_call'; name: string; input: unknown }
  /** errorCode：确认执行失败的代码（wire `error_code`，main-api 25a0c5b45）；summary 是给模型的句子，不当文案用 */
  | { type: 'tool_result'; name: string; ok: boolean; summary: string; errorCode?: string | null }
  | { type: 'confirmation_required'; pending: PendingConfirmation }
  | { type: 'sources'; items: ChatSource[] }
  | { type: 'done'; stop_reason: string }
  /**
   * message 只供日志（后端英文，不展示）；code 前端按码本地化（918cfe6d9）；
   * details 仅在后端带了时出现。
   */
  | { type: 'error'; message: string; code?: string | null; details?: unknown }
  | { type: 'flow_step'; step: FlowStep }
  /** 已过闭集白名单；一个块都没解析出来的帧不会产出事件 */
  | { type: 'ui_block'; blocks: UiBlock[] }

/** 一轮对话请求:会话历史由后端(Redis)持有,前端只发会话 id + 本轮文本。 */
export interface ChatTurnRequest {
  conversationId: string
  text: string
  /** 入口信封（main-api 3d4312421）。**每轮都带**；缺省 = body 里不出现 entry_context 键 */
  entryContext?: EntryContextWire
}

/** 启动一条 Flow（`start_flow`，与 text 二选一；main-api 57dfbf6c5 已上主线）。 */
export interface ChatStartFlowRequest {
  conversationId: string
  flowId: string
  /** 后端按 FlowDef.params 校验（未知键 422 invalid_params）；snake_case 原样透传 */
  params?: Record<string, unknown>
  /** 入口信封：start_flow 这一轮后端同样 adopt_if_new，subject 为 listing 时预填 listing_id */
  entryContext?: EntryContextWire
}

/** 放弃停在追问上的草稿（`cancel_flow`，main-api a14937e08） */
export interface ChatCancelFlowRequest {
  conversationId: string
  runId: SnowflakeId
}

/** 用户点了一个能起 Flow 的按钮（ui_block 的 start_flow chip / 建议按钮） */
export interface StartFlowIntent {
  flowId: string
  params: Record<string, unknown> | null
  /** 按钮上显示的文字：同时作为这一轮的用户气泡 */
  label: string
}

/** 确认 / 拒绝一次待确认写操作。响应与 /ai/chat/stream 同契约(SSE 续流)。 */
export interface ChatConfirmRequest {
  conversationId: string
  toolUseId: string
  decision: 'allow' | 'deny'
  /** 用户在卡片里改过的字段(只含 editable 的 key) */
  params?: Record<string, unknown>
  /** deny 时用户可选填的原因 */
  reason?: string
}

/**
 * 工具名 → 给用户看的进行中文案（i18n key 映射，渲染时取）。
 * 名字必须是后端工具清单里真有的（toolLabel.test.ts 按一份人工维护的工具名清单守着）。
 */
const TOOL_LABEL_KEYS: Record<string, string> = {
  search_tasks: 'chrome:aiChat.toolLabel.search_tasks',
  search_reservations: 'chrome:aiChat.toolLabel.search_reservations',
  get_shifts: 'chrome:aiChat.toolLabel.get_shifts',
  suggest_assignee: 'chrome:aiChat.toolLabel.suggest_assignee',
  search_knowledge_base: 'chrome:aiChat.toolLabel.search_knowledge_base',
  resolve_entity: 'chrome:aiChat.toolLabel.resolve_entity',
}
export const TOOL_LABEL_NAMES: readonly string[] = Object.keys(TOOL_LABEL_KEYS)

export const toolLabel = (name: string): string => {
  const key = TOOL_LABEL_KEYS[name]
  if (key) return i18n.t(key)
  // 内部工具名不上屏（C0 09-14）：通用文案 + 记下名字，漏配一眼能查到
  console.warn('[ai-chat] no progress label for tool, showing generic copy', { name })
  return i18n.t('chrome:aiChat.toolLabel.fallback')
}
